/**
 * Безопасность входа по всем компаниям (ЛЦ-12): сводка, действующие блокировки перебора паролей со снятием,
 * чаще всего ошибающиеся адреса и почты, журнал входов с отбором (90 дней).
 */
import {
  LOGIN_LOCK_KIND_TITLES,
  LOGIN_LOCK_RULES,
  type LoginEvent,
  type LoginEventFilter,
  type LoginLock,
  type SecurityOverview,
} from '@epc/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Panel, TitleBlock } from '../../ui/layout';
import { LoginList } from './UsersPage';

const FILTERS: Array<{ value: LoginEventFilter; title: string }> = [
  { value: 'all', title: 'Все' },
  { value: 'failed', title: 'Неудачные' },
  { value: 'ok', title: 'Удачные' },
  { value: 'new_device', title: 'Новые устройства' },
];

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function LocksPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['platform', 'security', 'locks'],
    queryFn: () => api<LoginLock[]>('/platform/security/locks', { query: { active: true } }),
  });
  const release = useMutation({
    mutationFn: (id: string) => api(`/platform/security/locks/${id}/release`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Блокировка снята');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  return (
    <Panel
      title="Действующие блокировки"
      meta={`${LOGIN_LOCK_RULES.thresholds.email} неудач по почте или ${LOGIN_LOCK_RULES.thresholds.ip} с адреса за ${LOGIN_LOCK_RULES.windowMinutes} минут — вход закрыт на ${LOGIN_LOCK_RULES.minutes.join(', ')} минут. Блокировка почты не держит человека на устройстве, с которого он уже входил: там свой счёт неудач`}
      bodyClassName="p-0"
    >
      {q.isPending ? (
        <div className="px-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <div className="p-5">
          <ErrorBox error={q.error} />
        </div>
      ) : !q.data.length ? (
        <p className="px-5 py-4 text-[14px] text-ink-2">Сейчас никто не заблокирован за перебор пароля.</p>
      ) : (
        <ul className="divide-y divide-line">
          {q.data.map((l) => (
            <li key={l.id} className="flex flex-col gap-2 px-5 py-2.5 sm:flex-row sm:items-center sm:gap-4">
              <div className="min-w-0 flex-1 text-[14px]">
                <span className="font-medium text-ink [overflow-wrap:anywhere]">
                  {l.kind === 'ip' ? `Адрес ${l.key}` : l.key}
                </span>
                <div className="text-[13px] text-ink-2">
                  {LOGIN_LOCK_KIND_TITLES[l.kind]} · {l.failures} неудач · ступень {l.level} · до{' '}
                  {dateTime(l.lockedUntil)}
                </div>
              </div>
              <Button
                size="sm"
                loading={release.isPending && release.variables === l.id}
                onClick={() => release.mutate(l.id)}
              >
                Снять
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function SecurityPage() {
  const [filter, setFilter] = useState<LoginEventFilter>('all');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(100);
  const q = useDebounced(search.trim());
  const overview = useQuery({
    queryKey: ['platform', 'security', 'overview'],
    queryFn: () => api<SecurityOverview>('/platform/security/overview'),
  });
  const events = useQuery({
    queryKey: ['platform', 'security', 'logins', filter, q, limit],
    queryFn: () =>
      api<LoginEvent[]>('/platform/security/logins', { query: { filter, q: q || undefined, limit } }),
    placeholderData: keepPreviousData,
  });
  const o = overview.data;
  return (
    <div className="flex flex-col gap-5">
      {overview.isError ? <ErrorBox error={overview.error} /> : null}
      {o ? (
        <TitleBlock
          className="rounded-lg border border-line bg-surface"
          items={[
            { label: 'Входов за 24 часа', value: o.logins24h, note: `за 7 дней: ${o.logins7d}` },
            {
              label: 'Неудачных за 24 часа',
              value: o.failed24h,
              note: `за 7 дней: ${o.failed7d}`,
              tone: o.failed24h ? 'accent' : 'default',
            },
            { label: 'Новых устройств', value: o.newDevices7d, note: 'за 7 дней' },
            {
              label: 'Заблокировано',
              value: o.activeLocks + o.blockedUsers,
              note: `перебор: ${o.activeLocks}, вход закрыт: ${o.blockedUsers}`,
              tone: o.activeLocks ? 'danger' : 'default',
            },
          ]}
        />
      ) : null}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Panel
          title="Журнал входов"
          meta={o ? `хранится ${o.retentionDays} дней` : undefined}
          actions={
            <Input
              type="search"
              aria-label="Почта или адрес"
              placeholder="Почта или адрес"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setLimit(100);
              }}
              className="w-full sm:w-56"
            />
          }
          bodyClassName="p-0"
        >
          <fieldset
            className="flex min-w-0 gap-1 overflow-x-auto border-b border-line px-3 py-2"
            aria-label="Отбор"
          >
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={filter === f.value}
                onClick={() => {
                  setFilter(f.value);
                  setLimit(100);
                }}
                className={clsx(
                  'h-11 shrink-0 rounded-md px-3 text-[13px] whitespace-nowrap md:h-8',
                  filter === f.value
                    ? 'bg-ink font-medium text-on-ink'
                    : 'text-ink-2 hover:bg-tile hover:text-ink',
                )}
              >
                {f.title}
              </button>
            ))}
          </fieldset>
          {events.isPending ? (
            <div className="px-5">
              <Spinner />
            </div>
          ) : events.isError ? (
            <div className="p-5">
              <ErrorBox error={events.error} />
            </div>
          ) : (
            <>
              <LoginList events={events.data} showWho />
              {events.data.length >= limit && limit < 500 ? (
                <div className="border-t border-line px-5 py-3">
                  <Button
                    size="sm"
                    loading={events.isFetching}
                    onClick={() => setLimit((l) => Math.min(500, l + 100))}
                  >
                    Показать ещё
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Panel>
        <div className="flex flex-col gap-5">
          <LocksPanel />
          {o && (o.topFailedIps.length || o.topFailedEmails.length) ? (
            <Panel title="Чаще всего ошибаются" meta="за 7 дней">
              <div className="flex flex-col gap-4 text-[14px]">
                {o.topFailedIps.length ? (
                  <div>
                    <div className="mb-1 text-[13px] text-ink-2">Адреса</div>
                    <ul className="flex flex-col gap-1">
                      {o.topFailedIps.map((r) => (
                        <li key={r.ip} className="flex justify-between gap-3">
                          <span className="[overflow-wrap:anywhere] text-ink">{r.ip}</span>
                          <span className="num text-ink-2">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {o.topFailedEmails.length ? (
                  <div>
                    <div className="mb-1 text-[13px] text-ink-2">Почты</div>
                    <ul className="flex flex-col gap-1">
                      {o.topFailedEmails.map((r) => (
                        <li key={r.email} className="flex justify-between gap-3">
                          <button
                            type="button"
                            className="min-h-11 text-left [overflow-wrap:anywhere] text-ink underline-offset-2 hover:underline md:min-h-0"
                            onClick={() => {
                              setSearch(r.email);
                              setFilter('all');
                            }}
                          >
                            {r.email}
                          </button>
                          <span className="num text-ink-2">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
